import { useCallback, useState, useEffect } from 'react';

interface State {
	customInputValue: string;
	isCustomInputFocused: boolean;
	currentValueSource: 'custom' | 'preset';
}

interface Params {
	currentValue: string;
}

/**
 * Sets up the internal state of a custom input + preset buttons combo (e.g. scaled order count).
 * Returns handlers for updating the state and some useful derived values.
 */
export default function useCustomInputWithPresets({ currentValue }: Params) {
	const [
		{ customInputValue, isCustomInputFocused, currentValueSource },
		setState,
	] = useState<State>({
		customInputValue: '',
		isCustomInputFocused: false,
		// Default to `preset`, as we don't highlight the custom input unless it's focused.
		currentValueSource: 'preset',
	});

	const updateStateFromCustomInput = useCallback((value: string) => {
		setState({
			customInputValue: value,
			isCustomInputFocused: true,
			currentValueSource: 'custom',
		});
	}, []);

	const updateStateFromPreset = useCallback((value: string) => {
		// Note, we don't set `isCustomInputFocused: false` here because we may
		// want to programatically call this while the user is inputting a custom
		// value (e.g. if their value is invalid and we want to revert back to
		// a preset being selected).
		setState((s) => ({
			...s,
			customInputValue: value,
			currentValueSource: 'preset',
		}));
	}, []);

	const updateCustomInputValue = useCallback((value: string) => {
		setState((s) => ({
			...s,
			customInputValue: value,
		}));
	}, []);

	const handleBlur = useCallback(() => {
		setState((s) => ({
			...s,
			isCustomInputFocused: false,
		}));
	}, []);

	const handleFocus = useCallback(() => {
		setState((s) => ({
			...s,
			isCustomInputFocused: true,
		}));
	}, []);

	// Update custom input value if external state is updated, unless the user is
	// already updating the value.
	useEffect(() => {
		if (isCustomInputFocused) {
			return;
		}

		updateCustomInputValue(currentValue?.toString() ?? '');
	}, [currentValue, isCustomInputFocused, updateCustomInputValue]);

	return {
		customInputValue,
		isCustomInputFocused,
		currentValueSource,
		updateStateFromCustomInput,
		updateStateFromPreset,
		updateCustomInputValue,
		handleBlur,
		handleFocus,
	};
}
